/**
 * "Where is this going?" — export destinations as data.
 *
 * Sizes are each marketplace's own published recommendation (checked
 * 2026-09-29): Amazon's main-image rules (pure white 255,255,255, the product
 * filling ~85% of the frame, ≥1000px for zoom), Shopify's 2048² square,
 * Etsy's 2700×2025 (4:3), Instagram's 4:5 feed and 9:16 story/reel,
 * Pinterest's 2:3 pin, the WhatsApp Business catalogue square.
 *
 * `fill` is how much of the frame's tighter side the ring spans, applied by
 * `fillFrame.ts` after rendering — so a 9:16 story is not a tiny ring in a
 * tall empty frame, and Amazon's 85% rule holds whatever the camera did.
 */
export type ExportBackground = "keep" | "white";

export interface ExportPreset {
  id: string;
  label: string;
  /** One short line: what it is for. */
  use: string;
  width: number;
  height: number;
  format: "jpeg" | "png";
  /** Fraction of the frame the ring spans (on whichever side binds). */
  fill: number;
  /** "white" forces a pure-white background regardless of the scene. */
  background: ExportBackground;
}

export const EXPORT_PRESETS: readonly ExportPreset[] = [
  { id: "amazon", label: "Amazon", use: "Main image · pure white, ring fills 85%", width: 2000, height: 2000, format: "jpeg", fill: 0.85, background: "white" },
  { id: "shopify", label: "Shopify", use: "Product photo · square", width: 2048, height: 2048, format: "jpeg", fill: 0.8, background: "keep" },
  { id: "etsy", label: "Etsy", use: "Listing photo · 4:3", width: 2700, height: 2025, format: "jpeg", fill: 0.78, background: "keep" },
  { id: "instagram", label: "Instagram post", use: "Feed · 4:5 portrait", width: 1080, height: 1350, format: "jpeg", fill: 0.75, background: "keep" },
  { id: "story", label: "Story / Reel", use: "Full screen · 9:16", width: 1080, height: 1920, format: "jpeg", fill: 0.72, background: "keep" },
  { id: "pinterest", label: "Pinterest", use: "Pin · 2:3", width: 1000, height: 1500, format: "jpeg", fill: 0.75, background: "keep" },
  { id: "whatsapp", label: "WhatsApp catalogue", use: "Catalogue item · square", width: 1080, height: 1080, format: "jpeg", fill: 0.8, background: "keep" },
  { id: "banner", label: "Website banner", use: "Wide header · 16:9", width: 1920, height: 1080, format: "jpeg", fill: 0.72, background: "keep" },
];

export function exportPreset(id: string): ExportPreset | undefined {
  return EXPORT_PRESETS.find((p) => p.id === id);
}

/** The shot list "All angles" walks, in the order a product page shows them. */
export const SHOT_LIST = [
  { id: "hero", label: "Hero ¾", camera: "threeQuarter" },
  { id: "front", label: "Front", camera: "front" },
  { id: "side", label: "Side", camera: "right" },
  { id: "top", label: "Top", camera: "top" },
] as const;

export type ShotId = (typeof SHOT_LIST)[number]["id"];

/** 360° video presets — a turntable, one full turn. */
export interface VideoPreset {
  id: string;
  label: string;
  use: string;
  width: number;
  height: number;
  seconds: number;
}

export const VIDEO_PRESETS: readonly VideoPreset[] = [
  { id: "reel", label: "Reel / Story", use: "9:16 · 10 seconds", width: 1080, height: 1920, seconds: 10 },
  { id: "square", label: "Square loop", use: "1:1 · 6 seconds", width: 1080, height: 1080, seconds: 6 },
  { id: "website", label: "Website", use: "16:9 · 8 seconds", width: 1920, height: 1080, seconds: 8 },
];

/** File-name safe slug of a model name, for downloads. */
export function fileStem(name: string | undefined | null): string {
  const base = (name ?? "ring").replace(/\.[a-z0-9]+$/i, "");
  return base.trim().replace(/[^a-z0-9]+/gi, "-").replace(/^-+|-+$/g, "").toLowerCase() || "ring";
}
